import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { cn } from "../../lib/utils.js";

interface AudioPlayerProps {
  src: string;
  onRefetch?: () => void;
  isRefetching?: boolean;
  className?: string;
  autoPlay?: boolean;
}

export function AudioPlayer({ src, onRefetch, isRefetching, className, autoPlay }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.load();
    setIsPlaying(false);
    setCurrent(0);
    setDuration(0);
    if (autoPlay) {
      void audio.play().catch(() => {
        /* autoplay can be blocked before the user interacts with the page */
      });
    }
  }, [src, autoPlay]);

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
  }

  function handleSeek(e: React.ChangeEvent<HTMLInputElement>) {
    const audio = audioRef.current;
    if (!audio) return;
    const value = Number(e.target.value);
    audio.currentTime = value;
    setCurrent(value);
  }

  function formatTime(s: number): string {
    if (!isFinite(s) || s <= 0) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  }

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2 rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2",
        className,
      )}
    >
      <audio
        ref={audioRef}
        src={src}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => { setIsPlaying(false); setCurrent(0); }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />

      <button
        type="button"
        onClick={togglePlay}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[var(--text-primary)] transition-colors hover:bg-white/20 active:scale-95"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying
          ? <Pause className="h-3.5 w-3.5" />
          : <Play className="h-3.5 w-3.5 translate-x-px" />}
      </button>

      <input
        type="range"
        min={0}
        max={duration || 100}
        step={0.01}
        value={current}
        onChange={handleSeek}
        className="h-1 flex-1 cursor-pointer appearance-none rounded-full [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white"
        style={{
          background: `linear-gradient(to right, var(--accent-via) ${progress}%, rgba(255,255,255,0.15) ${progress}%)`,
        }}
      />

      <span className="shrink-0 font-mono text-[10px] tabular-nums text-[var(--text-muted)]">
        {formatTime(current)}/{formatTime(duration)}
      </span>

      {onRefetch ? (
        <button
          type="button"
          onClick={onRefetch}
          disabled={isRefetching}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[var(--text-muted)] transition-colors hover:bg-white/10 hover:text-[var(--text-secondary)] disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Refetch audio"
        >
          <RotateCcw className={cn("h-3.5 w-3.5", isRefetching && "animate-spin")} />
        </button>
      ) : null}
    </div>
  );
}
